import 'twin.macro';
import 'styled-components/macro';
import { gql } from '@apollo/client/core';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { useNotificationContext } from 'core/context/Notifications';
import { SplashScreen } from 'play/organisms/splash-screen/SplashScreen';
import { isSkippingEnabled } from 'play/support/isSkippingEnabled';
import { useFinishButtonParameters } from 'play/support/useFinishButtonParameters';
import { SplashLayout } from 'play/templates/splash-layout/SplashLayout';
import { QueryParams } from 'support/QueryParams';

import { createPathPrefix } from '../../../support/location';
import { isChallengeActive } from '../../../support/track';
import { Mode, useSplashScreenQuery } from '../../../types/graphql';
import { ChallengeStatus, TrackStatus } from '../../../types/graphql';
import { LabMetadataContext } from '../../context/LabMetadataContext';
import { TrackNotFound } from '../../organisms/track-not-found/TrackNotFound';

export interface SplashScreenContainerProps {
  teamSlug: string;
  trackSlug: string;
  mode: Mode;
  inviteID?: string;
}

const SplashScreenContainer = ({ mode, teamSlug, trackSlug, inviteID }: SplashScreenContainerProps) => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const customParams = new URLSearchParams(Array.from(params).filter(([key]) => key.startsWith('icp_')));
  const showChallenges = params.has(QueryParams.SHOW_CHALLENGES);
  const embedToken = params.get(QueryParams.EMBED_TOKEN);
  const forceStart = params.get(QueryParams.FORCE_START) === 'true';
  const history = useHistory<{ force?: boolean; refererURL?: string; inviteID?: string }>();
  const { dispatch } = useNotificationContext();
  const [, setFinishButtonParameters, clearFinishButtonParameters] = useFinishButtonParameters();

  customParams.set(QueryParams.AUTO_START, 'true');

  useEffect(() => {
    const target = params.get(QueryParams.FINISH_BUTTON_TARGET) ?? '_blank';
    const text = params.get(QueryParams.FINISH_BUTTON_TEXT);
    const url = params.get(QueryParams.FINISH_BUTTON_URL);

    if (text && url) {
      setFinishButtonParameters({ target, text, url });
    } else {
      clearFinishButtonParameters();
    }
  }, [clearFinishButtonParameters, params, setFinishButtonParameters]);

  const { data, loading, error } = useSplashScreenQuery({
    variables: { teamSlug, trackSlug },
    fetchPolicy: 'cache-and-network',
    onCompleted(data) {
      if (embedToken) {
        const {
          location: { hostname },
        } = window;

        const domain = hostname.substring(hostname.indexOf('.'));
        const path = `/assets/tracks/${data.track?.id}`;

        // This is required for assets to work before the track is started
        document.cookie = `embed_token=${embedToken}; domain=${domain}; path=${path}; Secure`;
      }
    },
  });
  const currentChallenge = data?.track?.challenges.find(isChallengeActive);

  const onLaunchClicked = () => {
    if (!data?.track?.challenges[0]) {
      return;
    }

    history.push({
      pathname: `${createPathPrefix({ mode, inviteID })}${data.track.challenges[0].permalink}/notes`,
      search: customParams.toString(),
      state: {
        force: forceStart,
        refererURL: history.location.state?.refererURL,
      },
    });
  };

  useEffect(() => {
    if (!data) {
      return;
    }
  }, [trackSlug, teamSlug, mode, data, embedToken]);

  useEffect(() => {
    if (error?.message === 'Invalid or expired token') {
      dispatch('error', 'Error', error.message, 8);
    }
  }, [dispatch, error?.message]);

  if (!forceStart && data?.track?.status === TrackStatus.Started && !currentChallenge) {
    return <Redirect to={`${createPathPrefix({ mode, inviteID })}/${teamSlug}/tracks/${trackSlug}/recap`} />;
  }

  if (
    !forceStart &&
    [TrackStatus.Creating, TrackStatus.Created, TrackStatus.Started].some(s => s === data?.track?.status) &&
    [ChallengeStatus.Unlocked, ChallengeStatus.Creating, ChallengeStatus.Created].some(
      s => s === currentChallenge?.status,
    )
  ) {
    return <Redirect to={`${createPathPrefix({ mode, inviteID })}${currentChallenge?.permalink}/notes`} />;
  } else if (!forceStart && data?.track?.status === TrackStatus.Started && currentChallenge) {
    return <Redirect to={`${createPathPrefix({ mode, inviteID })}${currentChallenge.permalink}/assignment`} />;
  }

  if (['Entity not found', 'Unauthorized', 'Invalid or expired token'].some(m => error?.message.includes(m))) {
    if (error?.message) {
      Sentry.captureMessage(error.message);
    }
    return <TrackNotFound teamSlug={teamSlug} trackSlug={trackSlug} />;
  }

  if (loading) {
    return (
      <SplashLayout>
        <LoadingImage />
      </SplashLayout>
    );
  }

  return (
    <LabMetadataContext
      participantId={data?.track?.participant?.id}
      trackId={data?.track?.id}
      trackSkippable={isSkippingEnabled(data)}
      teamSlug={teamSlug}
      mode={mode}
    >
      {teamSlug === 'test-drive' && trackSlug === 'ansible-tower-demo' && (
        <Helmet>
          <title>{data?.track?.title ? `${data?.track?.title} - instruqt` : 'instruqt'}</title>
          <meta name="description" content={data?.track?.teaser} />

          <meta name="og:type" content="website" />
          <meta name="og:url" content={window.location.href} />
          <meta name="og:title" content={data?.track?.title ? `${data?.track?.title} - instruqt` : 'instruqt'} />
          <meta name="og:description" content={data?.track?.teaser} />
          <meta
            name="og:image"
            content="https://storage.googleapis.com/instruqt-frontend/img/bagdes/completion-bagde.svg"
          />

          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={window.location.toString()} />
          <meta
            property="twitter:title"
            content={data?.track?.title ? `${data?.track?.title} - instruqt` : 'instruqt'}
          />
          <meta property="twitter:description" content={data?.track?.teaser} />
          <meta
            property="twitter:image"
            content="https://storage.googleapis.com/instruqt-frontend/img/bagdes/completion-bagde.svg"
          />
        </Helmet>
      )}
      <SplashScreen
        challenges={showChallenges ? data?.track?.challenges : undefined}
        teaser={data?.track?.teaser}
        title={data?.track?.title}
        onLaunch={onLaunchClicked}
      />
    </LabMetadataContext>
  );
};

gql`
  query SplashScreen($teamSlug: String!, $trackSlug: String!) {
    track(teamSlug: $teamSlug, trackSlug: $trackSlug) {
      id
      title
      teaser
      status
      challenges {
        status
        slug
        started
        permalink
        completed
        title
      }
      participant {
        id
      }
    }

    ...IsSkippingEnabledQuery
  }
`;

export default SplashScreenContainer;
